import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ChevronRight, ChevronDown, Layers, Box, FileText, ListTodo,
  Plus, Edit2, Trash2, Check, X, AlertCircle, MoreHorizontal,
  Calendar, User, Flag, Clock, Keyboard
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useProductMode } from '../context/ProductModeContext';
import { toast } from 'sonner';
import { 
  GovernanceColumnsWrapper, 
  GovernanceColumnHeaders 
} from './GovernanceCountColumns';

// Status colors
const STATUS_CONFIG = {
  'Not Started': { color: 'var(--clr-sev-notstarted)', bg: 'var(--clr-sev-notstarted-bg)' },
  'In Progress': { color: 'var(--clr-sev-inprogress)', bg: 'var(--clr-sev-inprogress-bg)' },
  'In Review': { color: '#7C3AED', bg: '#F5F3FF' },
  'Blocked': { color: 'var(--clr-sev-blocked)', bg: 'var(--clr-sev-blocked-bg)' },
  'Behind': { color: 'var(--clr-sev-behind)', bg: 'var(--clr-sev-behind-bg)' },
  'Due Date at Risk': { color: 'var(--clr-sev-atrisk)', bg: 'var(--clr-sev-atrisk-bg)' },
  'Completed': { color: 'var(--clr-sev-completed)', bg: 'var(--clr-sev-completed-bg)' },
  'Archived': { color: '#9CA3AF', bg: '#F3F4F6' }
};

const STATUSES = Object.keys(STATUS_CONFIG);
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

// Type configurations
const TYPE_CONFIG = {
  epic: { label: 'Epic', icon: Layers, color: 'var(--clr-brand-primary)' },
  feature: { label: 'Feature', icon: Box, color: '#388E3C' },
  story: { label: 'Story', icon: FileText, color: '#1976D2' },
  task: { label: 'Task', icon: ListTodo, color: '#607D8B' }
};

// Keyboard shortcuts configuration
const KEYBOARD_SHORTCUTS = {
  NEW_ITEM: { key: 'n', ctrl: true, description: 'Quick-add new item' },
  EDIT: { key: 'e', ctrl: true, description: 'Edit selected row' },
  MOVE_UP: { key: 'ArrowUp', ctrl: true, shift: true, description: 'Move item up' },
  MOVE_DOWN: { key: 'ArrowDown', ctrl: true, shift: true, description: 'Move item down' },
  SAVE: { key: 'Enter', ctrl: true, description: 'Save inline edit' },
  CANCEL: { key: 'Escape', description: 'Cancel edit' },
  BULK_MODE: { key: 'b', ctrl: true, description: 'Toggle bulk selection' },
  DELETE: { key: 'Delete', ctrl: true, description: 'Delete item' }
};

/**
 * Inline editable cell component
 */
const InlineEditCell = ({ 
  value, 
  field, 
  type = 'text',
  options = [],
  onSave,
  canEdit = true,
  className = ''
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text') {
        inputRef.current.select();
      }
    }
  }, [editing, type]);
  
  useEffect(() => {
    setEditValue(value);
  }, [value]);
  
  const handleSave = () => {
    if (editValue !== value) {
      onSave(field, editValue);
    }
    setEditing(false);
  };
  
  const handleCancel = () => {
    setEditValue(value);
    setEditing(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  
  if (!canEdit) {
    return <span className={className}>{value || '-'}</span>;
  }
  
  if (type === 'status') {
    const config = STATUS_CONFIG[value] || STATUS_CONFIG['Not Started'];
    return editing ? (
      <select
        ref={inputRef}
        value={editValue}
        onChange={(e) => {
          setEditValue(e.target.value);
          onSave(field, e.target.value);
          setEditing(false);
        }}
        onBlur={handleCancel}
        className="px-2 py-1 text-xs rounded-full border-0 focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
        data-testid={`inline-edit-${field}`}
      >
        {STATUSES.map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    ) : (
      <button
        onClick={() => setEditing(true)}
        className="px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-opacity hover:opacity-80"
        style={{ backgroundColor: config.bg, color: config.color }}
        data-testid={`status-chip-${field}`}
      >
        {value || 'Not Started'}
      </button>
    );
  }
  
  if (type === 'select') {
    return editing ? (
      <select
        ref={inputRef}
        value={editValue || ''}
        onChange={(e) => {
          setEditValue(e.target.value);
          onSave(field, e.target.value);
          setEditing(false);
        }}
        onBlur={handleCancel}
        className="px-2 py-1 text-sm border border-[var(--clr-border-light)] rounded focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
        data-testid={`inline-edit-${field}`}
      >
        <option value="">-</option>
        {options.map(opt => (
          <option key={opt.value || opt} value={opt.value || opt}>
            {opt.label || opt}
          </option>
        ))}
      </select>
    ) : (
      <button
        onClick={() => setEditing(true)}
        className={`px-2 py-1 text-sm text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)] rounded ${className}`}
        data-testid={`inline-display-${field}`}
      >
        {value || '-'}
      </button>
    );
  }
  
  if (type === 'date') {
    return editing ? (
      <input
        ref={inputRef}
        type="date"
        value={editValue || ''}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="px-2 py-1 text-sm border border-[var(--clr-border-light)] rounded focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
        data-testid={`inline-edit-${field}`}
      />
    ) : (
      <button
        onClick={() => setEditing(true)}
        className={`px-2 py-1 text-sm text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)] rounded ${className}`}
        data-testid={`inline-display-${field}`}
      >
        {value ? new Date(value).toLocaleDateString() : '-'}
      </button>
    );
  }
  
  // Default text input
  return editing ? (
    <input
      ref={inputRef}
      type="text"
      value={editValue || ''}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      className={`px-2 py-1 text-sm border border-[var(--clr-border-light)] rounded focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent ${className}`}
      data-testid={`inline-edit-${field}`}
    />
  ) : (
    <button
      onClick={() => setEditing(true)}
      className={`px-2 py-1 text-sm text-left hover:bg-[var(--clr-surface-hover)] rounded truncate ${className}`}
      data-testid={`inline-display-${field}`}
    >
      {value || '-'}
    </button>
  );
};

/**
 * Quick Add Button - inline button to add child items
 */
const QuickAddButton = ({ type, onClick }) => {
  const config = TYPE_CONFIG[type];
  if (!config) return null;
  
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2 py-1 text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded transition-colors"
      data-testid={`quick-add-${type}`}
    >
      <Plus className="w-3 h-3" />
      Add {config.label}
    </button>
  );
};

/**
 * Single row in the hierarchy table - with drag-and-drop support
 */
const HierarchyRow = ({
  item,
  depth,
  expanded,
  onToggle,
  onInlineUpdate,
  onQuickAdd,
  onSelect,
  onDragStart,
  onDragOver,
  onDrop,
  onFocus,
  onOpenInspector,  // Callback to open Inspector at specific tab
  dragState,
  isSelected,
  isFocused,
  canEdit,
  storyLayerEnabled,
  sprints = [],
  releases = [],
  tenantId
}) => {
  const [saving, setSaving] = useState(false);
  const config = TYPE_CONFIG[item.type];
  const Icon = config?.icon || Layers;
  const hasChildren = item.children?.length > 0;
  const indentPx = depth * 24;
  
  // Check if this item is being dragged
  const isDragging = dragState?.draggedId === item.id;
  
  // Check if this is a valid drop target
  const isValidDropTarget = dragState?.validTargets?.includes(item.id);
  const isDropTargetActive = dragState?.dropTargetId === item.id;
  
  // Calculate overdue status
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = item.due_date ? new Date(item.due_date) : null;
  const isOverdue = dueDate && dueDate < today && item.status !== 'Completed';
  const isDueSoon = dueDate && !isOverdue && (dueDate - today) / (1000 * 60 * 60 * 24) <= 7 && item.status !== 'Completed';
  
  // Determine if this item can be dragged
  const canDrag = canEdit && item.type !== 'epic'; // Epics cannot be dragged
  
  // Determine if items can be dropped here
  const canBeDropTarget = (draggedType) => {
    if (!draggedType) return false;
    // Features can be dropped on epics
    if (item.type === 'epic' && draggedType === 'feature') return true;
    // Stories can be dropped on features
    if (item.type === 'feature' && draggedType === 'story') return true;
    // Tasks can be dropped on stories or features (XOR rule)
    if ((item.type === 'story' || item.type === 'feature') && draggedType === 'task') return true;
    return false;
  };
  
  const handleDragStart = (e) => {
    if (!canDrag) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      id: item.id,
      type: item.type,
      name: item.name
    }));
    onDragStart(item);
  };
  
  const handleDragOver = (e) => {
    if (!canBeDropTarget(dragState?.draggedType)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(item);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    if (!canBeDropTarget(dragState?.draggedType)) return;
    onDrop(item);
  };
  
  const handleDragEnd = () => {
    // Reset drag state handled by parent
  };
  
  // Handle inline save with optimistic update
  const handleSave = async (field, value) => {
    setSaving(true);
    const previousValue = item[field];
    
    // Optimistic update - immediately show the change
    onInlineUpdate(item.id, item.type, field, value, true);
    
    try {
      await api.inlineUpdateWorkItem(item.type, item.id, {
        [field]: value,
        tenant_id: item.tenant_id
      });
      
      toast.success(`${config?.label} updated`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await api.inlineUpdateWorkItem(item.type, item.id, {
                [field]: previousValue,
                tenant_id: item.tenant_id
              });
              onInlineUpdate(item.id, item.type, field, previousValue, true);
              toast.success('Change reverted');
            } catch (err) {
              toast.error('Failed to undo');
            }
          }
        },
        duration: 5000
      });
    } catch (error) {
      // Revert optimistic update
      onInlineUpdate(item.id, item.type, field, previousValue, true);
      toast.error('Failed to save change');
    } finally {
      setSaving(false);
    }
  };
  
  // Determine what child types can be added
  const getAddableChildTypes = () => {
    if (item.type === 'epic') return ['feature'];
    if (item.type === 'feature') return storyLayerEnabled ? ['story', 'task'] : ['task'];
    if (item.type === 'story') return ['task'];
    return [];
  };
  
  const addableTypes = getAddableChildTypes();
  
  return (
    <>
      <tr 
        draggable={canDrag}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        onClick={() => onFocus && onFocus(item.id)}
        className={`group border-b border-[var(--clr-border-light)] transition-colors ${
          isDragging ? 'opacity-50 bg-gray-100' : 'hover:bg-[var(--clr-surface-hover)]'
        } ${isOverdue ? 'bg-red-50' : isDueSoon ? 'bg-yellow-50' : ''
        } ${isSelected ? 'bg-blue-50' : ''
        } ${isFocused ? 'ring-2 ring-[var(--clr-brand-primary)] ring-inset bg-blue-50/50' : ''
        } ${isDropTargetActive ? 'bg-blue-100 ring-2 ring-[var(--clr-brand-primary)] ring-inset' : ''
        } ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
        data-testid={`hierarchy-row-${item.id}`}
      >
        {/* Checkbox */}
        <td className="w-10 px-2 py-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(item.id, e.target.checked)}
            className="w-4 h-4 text-[var(--clr-brand-primary)] rounded border-[var(--clr-border-medium)] focus:ring-[var(--clr-brand-primary)]"
            data-testid={`row-checkbox-${item.id}`}
          />
        </td>
        
        {/* Name with expand/collapse and drag handle */}
        <td className="py-2 pr-4">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indentPx}px` }}>
            {/* Drag handle indicator for draggable items */}
            {canDrag && (
              <span className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab mr-1">
                ⋮⋮
              </span>
            )}
            
            {/* Expand/Collapse button */}
            {hasChildren ? (
              <button
                onClick={() => onToggle(item.id)}
                className="p-1 hover:bg-[var(--clr-surface-active)] rounded"
                data-testid={`toggle-${item.id}`}
              >
                {expanded ? (
                  <ChevronDown className="w-4 h-4 text-[var(--clr-text-muted)]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)]" />
                )}
              </button>
            ) : (
              <span className="w-6" />
            )}
            
            {/* Type icon */}
            <div 
              className="flex items-center justify-center w-6 h-6 rounded shrink-0"
              style={{ backgroundColor: `${config?.color}15` }}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: config?.color }} />
            </div>
            
            {/* Name */}
            <InlineEditCell
              value={item.name}
              field="name"
              onSave={handleSave}
              canEdit={canEdit}
              className="font-medium text-[var(--clr-text-primary)] max-w-[300px]"
            />
            
            {/* Overdue/Due Soon badge */}
            {isOverdue && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[var(--clr-sev-blocked)] text-white rounded">
                OVERDUE
              </span>
            )}
            {isDueSoon && !isOverdue && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[var(--clr-sev-atrisk)] text-white rounded">
                DUE SOON
              </span>
            )}
            
            {saving && (
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-[var(--clr-brand-primary)] border-t-transparent ml-2" />
            )}
          </div>
        </td>
        
        {/* Status */}
        <td className="py-2 px-2">
          <InlineEditCell
            value={item.status}
            field="status"
            type="status"
            onSave={handleSave}
            canEdit={canEdit}
          />
        </td>
        
        {/* Priority */}
        <td className="py-2 px-2">
          <InlineEditCell
            value={item.priority}
            field="priority"
            type="select"
            options={PRIORITIES}
            onSave={handleSave}
            canEdit={canEdit}
          />
        </td>
        
        {/* Assignee/Owner */}
        <td className="py-2 px-2">
          <InlineEditCell
            value={item.assignee || item.owner}
            field={item.type === 'task' ? 'assignee' : 'owner'}
            onSave={handleSave}
            canEdit={canEdit}
            className="max-w-[120px]"
          />
        </td>
        
        {/* Due Date */}
        <td className="py-2 px-2">
          <InlineEditCell
            value={item.due_date}
            field="due_date"
            type="date"
            onSave={handleSave}
            canEdit={canEdit}
          />
        </td>
        
        {/* Governance Columns - R/I/D/Dep (hidden on mobile) */}
        <GovernanceColumnsWrapper
          workId={item.id}
          tenantId={tenantId}
          onOpenInspector={onOpenInspector}
          hasPermission={canEdit}
          className="hidden lg:table-cell"
        />
        
        {/* Quick Add buttons */}
        <td className="py-2 px-2">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {addableTypes.map(childType => (
              <QuickAddButton
                key={childType}
                type={childType}
                onClick={() => onQuickAdd(item, childType)}
              />
            ))}
          </div>
        </td>
      </tr>
      
      {/* Render children if expanded */}
      {expanded && item.children?.map(child => (
        <HierarchyRow
          key={child.id}
          item={child}
          depth={depth + 1}
          expanded={child._expanded}
          onToggle={onToggle}
          onInlineUpdate={onInlineUpdate}
          onQuickAdd={onQuickAdd}
          onSelect={onSelect}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onFocus={onFocus}
          onOpenInspector={onOpenInspector}
          dragState={dragState}
          isSelected={child._selected}
          isFocused={child._focused}
          canEdit={canEdit}
          storyLayerEnabled={storyLayerEnabled}
          sprints={sprints}
          releases={releases}
          tenantId={tenantId}
        />
      ))}
    </>
  );
};

/**
 * HierarchyTable - Main component for displaying and editing work item hierarchy
 */
export const HierarchyTable = ({
  projectId,
  tenantId,
  typeFilter = 'all', // Sprint 24: Type filter from URL
  onQuickAdd,
  onBulkAction,
  onOpenInspector,  // Callback to open Inspector at specific tab
  className = ''
}) => {
  const { canEdit: canEditCheck, isStoryLayerEnabled: isStoryLayerEnabledFn } = useTenant();
  const { getFeature } = useProductMode();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [storyLayerEnabled, setStoryLayerEnabled] = useState(true);
  const [sprints, setSprints] = useState([]);
  const [releases, setReleases] = useState([]);
  
  // Drag-and-drop state
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedId: null,
    draggedType: null,
    draggedItem: null,
    dropTargetId: null,
    validTargets: []
  });
  
  // Keyboard shortcuts state
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const tableRef = useRef(null);
  
  const canEdit = canEditCheck();
  
  // Load hierarchy data
  const loadHierarchy = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Sprint 24: Include type filter in API call
      const filterParams = { depth: 4 };
      if (typeFilter && typeFilter !== 'all') {
        filterParams.type = typeFilter;
      }
      
      const [hierarchyRes, sprintsRes, releasesRes] = await Promise.all([
        api.getProjectWorkHierarchy(projectId, filterParams),
        api.getSprints({ project_id: projectId }),
        api.getReleases({ project_id: projectId })
      ]);
      
      setHierarchy(hierarchyRes.data.hierarchy || []);
      setStoryLayerEnabled(hierarchyRes.data.story_layer_enabled);
      setSprints(sprintsRes.data.sprints || sprintsRes.data || []);
      setReleases(releasesRes.data.releases || releasesRes.data || []);
      
      // Expand all epics by default
      const epicIds = new Set(hierarchyRes.data.hierarchy?.map(e => e.id) || []);
      setExpandedIds(epicIds);
    } catch (err) {
      console.error('Failed to load hierarchy:', err);
      setError('Failed to load work items');
    } finally {
      setLoading(false);
    }
  }, [projectId, typeFilter]);
  
  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);
  
  // Toggle expand/collapse
  const handleToggle = (itemId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };
  
  // Handle inline update with optimistic UI
  const handleInlineUpdate = (itemId, itemType, field, value, isOptimistic = false) => {
    const updateItemInHierarchy = (items) => {
      return items.map(item => {
        if (item.id === itemId) {
          return { ...item, [field]: value };
        }
        if (item.children?.length) {
          return { ...item, children: updateItemInHierarchy(item.children) };
        }
        return item;
      });
    };
    
    setHierarchy(prev => updateItemInHierarchy(prev));
  };
  
  // Handle quick add
  const handleQuickAdd = (parent, childType) => {
    if (onQuickAdd) {
      onQuickAdd(parent, childType, () => loadHierarchy());
    }
  };
  
  // Handle selection
  const handleSelect = (itemId, selected) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  };
  
  // ==================== DRAG-AND-DROP HANDLERS ====================
  
  // Get all valid drop target IDs for a given item type
  const getValidDropTargetIds = (draggedType) => {
    const validIds = [];
    
    const collectTargets = (items) => {
      for (const item of items) {
        // Features can be dropped on epics
        if (item.type === 'epic' && draggedType === 'feature') {
          validIds.push(item.id);
        }
        // Stories can be dropped on features
        if (item.type === 'feature' && draggedType === 'story') {
          validIds.push(item.id);
        }
        // Tasks can be dropped on stories or features
        if ((item.type === 'story' || item.type === 'feature') && draggedType === 'task') {
          validIds.push(item.id);
        }
        if (item.children) {
          collectTargets(item.children);
        }
      }
    };
    
    collectTargets(hierarchy);
    return validIds;
  };
  
  // Handle drag start
  const handleDragStart = (item) => {
    const validTargets = getValidDropTargetIds(item.type);
    setDragState({
      isDragging: true,
      draggedId: item.id,
      draggedType: item.type,
      draggedItem: item,
      dropTargetId: null,
      validTargets
    });
  };
  
  // Handle drag over (hovering over potential drop target)
  const handleDragOver = (targetItem) => {
    if (!dragState.isDragging) return;
    if (!dragState.validTargets.includes(targetItem.id)) return;
    
    setDragState(prev => ({
      ...prev,
      dropTargetId: targetItem.id
    }));
  };
  
  // Handle drop - perform the re-parenting
  const handleDrop = async (targetItem) => {
    if (!dragState.isDragging || !dragState.draggedItem) return;
    if (!dragState.validTargets.includes(targetItem.id)) return;
    
    const draggedItem = dragState.draggedItem;
    
    // Reset drag state immediately for better UX
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedType: null,
      draggedItem: null,
      dropTargetId: null,
      validTargets: []
    });
    
    // Don't drop on same parent
    const currentParentId = draggedItem.story_id || draggedItem.feature_id || draggedItem.epic_id;
    if (currentParentId === targetItem.id) {
      return;
    }
    
    try {
      const result = await api.reparentWorkItem({
        item_type: draggedItem.type,
        item_id: draggedItem.id,
        new_parent_type: targetItem.type,
        new_parent_id: targetItem.id,
        tenant_id: tenantId
      });
      
      if (result.data.moved) {
        toast.success(`${TYPE_CONFIG[draggedItem.type]?.label} moved to ${targetItem.name}`, {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                await api.reparentWorkItem({
                  item_type: draggedItem.type,
                  item_id: draggedItem.id,
                  new_parent_type: result.data.old_parent.type,
                  new_parent_id: result.data.old_parent.id,
                  tenant_id: tenantId
                });
                loadHierarchy();
                toast.success('Move reverted');
              } catch (err) {
                toast.error('Failed to undo move');
              }
            }
          },
          duration: 5000
        });
        
        // Reload hierarchy to reflect changes
        loadHierarchy();
      }
    } catch (error) {
      console.error('Failed to reparent item:', error);
      toast.error(error.response?.data?.detail || 'Failed to move item');
    }
  };
  
  // Handle drag end (cleanup if dropped outside valid target)
  const handleDragEnd = () => {
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedType: null,
      draggedItem: null,
      dropTargetId: null,
      validTargets: []
    });
  };
  
  // ==================== KEYBOARD SHORTCUTS ====================
  
  // Flatten hierarchy for navigation
  const getFlattenedItems = useCallback(() => {
    const flat = [];
    const flatten = (items, depth = 0) => {
      for (const item of items) {
        flat.push({ ...item, depth });
        if (item.children && expandedIds.has(item.id)) {
          flatten(item.children, depth + 1);
        }
      }
    };
    flatten(hierarchy);
    return flat;
  }, [hierarchy, expandedIds]);
  
  // Get focused item
  const getFocusedItem = useCallback(() => {
    if (!focusedItemId) return null;
    const flat = getFlattenedItems();
    return flat.find(item => item.id === focusedItemId);
  }, [focusedItemId, getFlattenedItems]);
  
  // Navigate to next/previous item
  const navigateItems = useCallback((direction) => {
    const flat = getFlattenedItems();
    if (flat.length === 0) return;
    
    const currentIndex = flat.findIndex(item => item.id === focusedItemId);
    let newIndex;
    
    if (currentIndex === -1) {
      newIndex = direction === 'up' ? flat.length - 1 : 0;
    } else {
      newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0) newIndex = flat.length - 1;
      if (newIndex >= flat.length) newIndex = 0;
    }
    
    setFocusedItemId(flat[newIndex].id);
  }, [focusedItemId, getFlattenedItems]);
  
  // Move item up/down within its level (Ctrl+Shift+↑/↓)
  const handleMoveItem = useCallback(async (direction) => {
    if (!canEdit || !focusedItemId) return;
    
    const focused = getFocusedItem();
    if (!focused) return;
    
    try {
      const result = await api.reorderWorkItem({
        item_type: focused.type,
        item_id: focused.id,
        direction: direction,
        tenant_id: tenantId
      });
      
      if (result.data.reordered) {
        toast.success(`${TYPE_CONFIG[focused.type]?.label} moved ${direction}`);
        loadHierarchy();
      } else {
        toast.info(result.data.message || 'Cannot move further');
      }
    } catch (error) {
      toast.error('Failed to move item');
    }
  }, [canEdit, focusedItemId, getFocusedItem, tenantId, loadHierarchy]);
  
  // Delete item with confirmation
  const handleDeleteItem = useCallback(async (item) => {
    if (!canEdit || !item) return;
    
    try {
      // Determine collection based on type
      let endpoint;
      if (item.type === 'epic') {
        endpoint = `/epics/${item.id}`;
      } else if (item.type === 'feature') {
        endpoint = `/features/${item.id}`;
      } else {
        endpoint = `/workitems/${item.id}`;
      }
      
      await api.deleteWorkItem(item.type, item.id);
      toast.success(`${TYPE_CONFIG[item.type]?.label} deleted`);
      loadHierarchy();
      setShowDeleteConfirm(null);
      setFocusedItemId(null);
    } catch (error) {
      toast.error('Failed to delete item');
    }
  }, [canEdit, loadHierarchy]);
  
  // Quick add new item (context-aware)
  const handleQuickAddNew = useCallback(() => {
    if (!canEdit) return;
    
    const focused = getFocusedItem();
    if (focused && onQuickAdd) {
      // Add child based on focused item type
      if (focused.type === 'epic') {
        onQuickAdd(focused, 'feature', loadHierarchy);
      } else if (focused.type === 'feature') {
        onQuickAdd(focused, storyLayerEnabled ? 'story' : 'task', loadHierarchy);
      } else if (focused.type === 'story') {
        onQuickAdd(focused, 'task', loadHierarchy);
      }
    } else if (onQuickAdd) {
      // Add top-level epic
      toast.info('Select an item first, or use the Composer to create new epics');
    }
  }, [canEdit, getFocusedItem, onQuickAdd, storyLayerEnabled, loadHierarchy]);
  
  // Toggle edit mode for focused item
  const handleToggleEdit = useCallback(() => {
    if (!canEdit || !focusedItemId) return;
    
    if (editingItemId === focusedItemId) {
      setEditingItemId(null);
    } else {
      setEditingItemId(focusedItemId);
    }
  }, [canEdit, focusedItemId, editingItemId]);
  
  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts when typing in input fields (except Escape)
      const isInputFocused = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      
      // Escape always works (cancel edit)
      if (e.key === 'Escape') {
        if (editingItemId) {
          setEditingItemId(null);
          e.preventDefault();
        }
        if (showDeleteConfirm) {
          setShowDeleteConfirm(null);
          e.preventDefault();
        }
        return;
      }
      
      // Skip other shortcuts if typing
      if (isInputFocused) return;
      
      // Ctrl+N: Quick-add new item
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        handleQuickAddNew();
        return;
      }
      
      // Ctrl+E: Edit selected row
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        handleToggleEdit();
        return;
      }
      
      // Ctrl+B: Toggle bulk selection mode
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        setBulkMode(prev => !prev);
        toast.info(bulkMode ? 'Bulk selection mode OFF' : 'Bulk selection mode ON');
        return;
      }
      
      // Ctrl+Delete: Delete item
      if (e.ctrlKey && e.key === 'Delete') {
        e.preventDefault();
        if (focusedItemId && canEdit) {
          const item = getFocusedItem();
          if (item) {
            setShowDeleteConfirm(item);
          }
        }
        return;
      }
      
      // Arrow keys for navigation
      if (e.key === 'ArrowUp' && !e.ctrlKey) {
        e.preventDefault();
        navigateItems('up');
        return;
      }
      
      if (e.key === 'ArrowDown' && !e.ctrlKey) {
        e.preventDefault();
        navigateItems('down');
        return;
      }
      
      // Ctrl+Shift+ArrowUp/Down: Move item within its level
      if (e.ctrlKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const direction = e.key === 'ArrowUp' ? 'up' : 'down';
        handleMoveItem(direction);
        return;
      }
      
      // Enter: Toggle expand or start edit
      if (e.key === 'Enter' && !e.ctrlKey) {
        e.preventDefault();
        if (focusedItemId) {
          const item = getFocusedItem();
          if (item?.children?.length > 0) {
            handleToggle(item.id);
          } else if (canEdit) {
            setEditingItemId(focusedItemId);
          }
        }
        return;
      }
      
      // Space: Toggle selection
      if (e.key === ' ' && focusedItemId) {
        e.preventDefault();
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(focusedItemId)) {
            next.delete(focusedItemId);
          } else {
            next.add(focusedItemId);
          }
          return next;
        });
        return;
      }
      
      // ? : Show keyboard shortcuts help
      if (e.key === '?') {
        e.preventDefault();
        setShowShortcutsHelp(prev => !prev);
        return;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    canEdit, focusedItemId, editingItemId, bulkMode, showDeleteConfirm,
    handleQuickAddNew, handleToggleEdit, navigateItems, getFocusedItem, handleToggle, handleMoveItem
  ]);
  
  // Select all visible items
  const handleSelectAll = () => {
    const getAllIds = (items) => {
      let ids = [];
      for (const item of items) {
        ids.push(item.id);
        if (item.children && expandedIds.has(item.id)) {
          ids = ids.concat(getAllIds(item.children));
        }
      }
      return ids;
    };
    
    const allIds = getAllIds(hierarchy);
    if (selectedIds.size === allIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };
  
  // Mark items with expanded/selected/focused state
  const markHierarchy = (items) => {
    return items.map(item => ({
      ...item,
      _expanded: expandedIds.has(item.id),
      _selected: selectedIds.has(item.id),
      _focused: focusedItemId === item.id,
      children: item.children ? markHierarchy(item.children) : []
    }));
  };
  
  const markedHierarchy = markHierarchy(hierarchy);
  
  // Expand all
  const handleExpandAll = () => {
    const getAllIds = (items) => {
      let ids = [];
      for (const item of items) {
        ids.push(item.id);
        if (item.children) {
          ids = ids.concat(getAllIds(item.children));
        }
      }
      return ids;
    };
    setExpandedIds(new Set(getAllIds(hierarchy)));
  };
  
  // Collapse all
  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };
  
  if (loading) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-8 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--clr-brand-primary)] border-t-transparent" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-8 ${className}`}>
        <div className="flex items-center justify-center gap-3 text-[var(--clr-sev-blocked)]">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }
  
  // Sprint 24: Empty state when no items match the type filter
  if (!loading && hierarchy.length === 0) {
    // typeFilter values are already plural (epics, features, stories, tasks)
    const typeLabel = typeFilter && typeFilter !== 'all' 
      ? typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)
      : 'items';
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-12 ${className}`} data-testid="hierarchy-empty-state">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--clr-surface-tint)] mb-4">
            <Layers className="w-8 h-8 text-[var(--clr-text-muted)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--clr-text-primary)] mb-2">
            No {typeLabel} found
          </h3>
          <p className="text-[var(--clr-text-muted)] text-sm max-w-sm mx-auto">
            {typeFilter && typeFilter !== 'all' 
              ? `No ${typeLabel.toLowerCase()} match your current filters. Try selecting a different type or clearing filters.`
              : 'This project has no work items yet. Create your first epic to get started.'
            }
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden ${className}`} data-testid="hierarchy-table">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--clr-border-light)]">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-[var(--clr-text-primary)]">Work Item Hierarchy</h3>
          <span className="text-sm text-[var(--clr-text-muted)]">
            {hierarchy.length} epic{hierarchy.length !== 1 ? 's' : ''}
          </span>
          {canEdit && (
            <span className="text-xs text-[var(--clr-text-muted)] bg-[var(--clr-surface-subtle)] px-2 py-1 rounded">
              Drag items to re-parent
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowShortcutsHelp(true)}
            className="px-3 py-1.5 text-sm text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors flex items-center gap-1"
            title="Keyboard Shortcuts (?)"
            data-testid="shortcuts-help"
          >
            <Keyboard className="w-4 h-4" />
            <span className="hidden sm:inline">Shortcuts</span>
          </button>
          <button
            onClick={handleExpandAll}
            className="px-3 py-1.5 text-sm text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            data-testid="expand-all"
          >
            Expand All
          </button>
          <button
            onClick={handleCollapseAll}
            className="px-3 py-1.5 text-sm text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            data-testid="collapse-all"
          >
            Collapse All
          </button>
          <button
            onClick={loadHierarchy}
            className="px-3 py-1.5 text-sm text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            data-testid="refresh-hierarchy"
          >
            Refresh
          </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[var(--clr-surface-subtle)]">
            <tr className="border-b border-[var(--clr-border-light)]">
              <th className="w-10 px-2 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === hierarchy.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-[var(--clr-brand-primary)] rounded border-[var(--clr-border-medium)] focus:ring-[var(--clr-brand-primary)]"
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Name
              </th>
              <th className="py-3 px-2 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Status
              </th>
              <th className="py-3 px-2 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Priority
              </th>
              <th className="py-3 px-2 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Assignee
              </th>
              <th className="py-3 px-2 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Due Date
              </th>
              {/* Governance Columns - R/I/D/Dep */}
              <GovernanceColumnHeaders />
              <th className="py-3 px-2 text-left text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {markedHierarchy.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-[var(--clr-text-muted)]">
                  <div className="flex flex-col items-center gap-3">
                    <Layers className="w-12 h-12 text-[var(--clr-border-medium)]" />
                    <p>No work items found</p>
                    <p className="text-sm">Use the composer above to create your first epic</p>
                  </div>
                </td>
              </tr>
            ) : (
              markedHierarchy.map(item => (
                <HierarchyRow
                  key={item.id}
                  item={item}
                  depth={0}
                  expanded={item._expanded}
                  onToggle={handleToggle}
                  onInlineUpdate={handleInlineUpdate}
                  onQuickAdd={handleQuickAdd}
                  onSelect={handleSelect}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onFocus={setFocusedItemId}
                  onOpenInspector={onOpenInspector}
                  dragState={dragState}
                  isSelected={item._selected}
                  isFocused={item._focused}
                  canEdit={canEdit}
                  storyLayerEnabled={storyLayerEnabled}
                  sprints={sprints}
                  releases={releases}
                  tenantId={tenantId}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-4" data-testid="bulk-action-bar">
          <span className="font-medium">{selectedIds.size} selected</span>
          
          <select
            onChange={(e) => {
              if (e.target.value && onBulkAction) {
                onBulkAction('status', e.target.value, Array.from(selectedIds));
                e.target.value = '';
              }
            }}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm border-none"
          >
            <option value="">Set Status...</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          
          <select
            onChange={(e) => {
              if (e.target.value && onBulkAction) {
                onBulkAction('priority', e.target.value, Array.from(selectedIds));
                e.target.value = '';
              }
            }}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm border-none"
          >
            <option value="">Set Priority...</option>
            {PRIORITIES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 hover:bg-gray-700 rounded-lg text-sm flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        </div>
      )}
      
      {/* Keyboard Shortcuts Help Modal */}
      {showShortcutsHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowShortcutsHelp(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div className="flex items-center gap-3">
                <Keyboard className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                <h3 className="font-semibold text-[var(--clr-text-primary)]">Keyboard Shortcuts</h3>
              </div>
              <button onClick={() => setShowShortcutsHelp(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-96 overflow-y-auto">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Quick-add new item</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl + N</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Edit selected row</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl + E</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Toggle bulk selection</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl + B</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Delete item</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl + Del</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Navigate up/down</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">↑ / ↓</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Move item up/down</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Ctrl+Shift + ↑/↓</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Expand/Edit</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Enter</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Toggle selection</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Space</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Cancel/Close</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Esc</kbd>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--clr-text-secondary)]">Show this help</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">?</kbd>
              </div>
            </div>
            <div className="px-5 py-3 bg-gray-50 text-center text-xs text-[var(--clr-text-muted)]">
              Press <kbd className="px-1 bg-gray-200 rounded">?</kbd> anytime to toggle this help
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b">
              <h3 className="font-semibold text-[var(--clr-text-primary)]">Delete {TYPE_CONFIG[showDeleteConfirm.type]?.label}?</h3>
            </div>
            <div className="p-5">
              <p className="text-[var(--clr-text-secondary)] mb-4">
                Are you sure you want to delete "<strong>{showDeleteConfirm.name}</strong>"?
                {showDeleteConfirm.children?.length > 0 && (
                  <span className="text-[var(--clr-sev-blocked)] block mt-2">
                    This will also delete {showDeleteConfirm.children.length} child item(s).
                  </span>
                )}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 text-sm text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteItem(showDeleteConfirm)}
                  className="px-4 py-2 text-sm bg-[var(--clr-sev-blocked)] text-white rounded-lg hover:opacity-90"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyTable;
